import zoomSdk, { type ConfigResponse } from '@zoom/appssdk';
import { withTimeout } from './promise-timeout';

export { zoomSdk };

/** Capabilities must match Zoom Marketplace → Feature → Zoom App SDK (Development AND Production). */
export const ZOOM_SDK_CAPABILITIES = [
  'getRunningContext',
  'getUserContext',
  'getAppContext',
  'getMeetingParticipants',
  'getMeetingContext',
  'getMeetingUUID',
  'onParticipantChange',
  'onMyUserContextChange',
  'promptAuthorize',
] as const;

export type ZoomSdkConfigResponse = ConfigResponse;

const CONFIG_TIMEOUT_MS = 20_000;

let configured: ZoomSdkConfigResponse | null = null;
let configInFlight: Promise<ZoomSdkConfigResponse | null> | null = null;
let lastConfigError: string | undefined;

export function getLastConfigError(): string | undefined {
  return lastConfigError;
}

/** True when the panel is embedded (e.g. inside the Zoom client). */
export function isLikelyEmbeddedInZoom(): boolean {
  try {
    return window.parent !== window || window.self !== window.top;
  } catch {
    return true;
  }
}

/** Whether startup diagnostic codes (ZCS:*) should be shown. */
export function shouldShowZoomDiagnostics(): boolean {
  // Prod builds are only deployed for Zoom; the webview is often top-level (not an iframe).
  if (import.meta.env.PROD) return true;
  return isLikelyEmbeddedInZoom();
}

/**
 * Call zoomSdk.config exactly once per page load. Zoom requires config to finish
 * before any other SDK method — do not invoke config() again on timeout.
 */
export async function ensureZoomSdkConfigured(): Promise<ZoomSdkConfigResponse | null> {
  if (configured) return configured;
  if (configInFlight) return configInFlight;

  configInFlight = (async () => {
    try {
      const response = await withTimeout(
        async () =>
          zoomSdk.config({
            popoutSize: { width: 480, height: 360 },
            capabilities: [...ZOOM_SDK_CAPABILITIES],
          }),
        CONFIG_TIMEOUT_MS,
        'config',
      );
      configured = response;
      lastConfigError = undefined;
      return configured;
    } catch (err) {
      lastConfigError = err instanceof Error ? err.message : 'config failed';
      return null;
    } finally {
      configInFlight = null;
    }
  })();

  return configInFlight;
}

/** Re-run zoomSdk.config after promptAuthorize / onMyUserContextChange (per Zoom docs). */
export function resetZoomSdkConfigured(): void {
  configured = null;
  configInFlight = null;
}
